'use client';

import React, { useEffect, useState } from 'react';

interface Employee {
  id: string;
  name: string;
  role: string;
  avatarInitials: string;
}

interface SessionEntry {
  id: string;
  clockIn: string;
  clockOut: string | null;
  duration: string | null;
}

interface ClockSuccessStateProps {
  employee: Employee;
  action: 'clock_in' | 'clock_out';
  recordedTime: string;
  sessions: SessionEntry[];
  onDone: () => void;
}

export default function ClockSuccessState({
  employee,
  action,
  recordedTime,
  sessions,
  onDone,
}: ClockSuccessStateProps) {
  const [visible, setVisible] = useState(false);
  const isClockedIn = action === 'clock_in';

  useEffect(() => {
    setVisible(true);
    const t = setTimeout(() => onDone(), 6000);
    return () => clearTimeout(t);
  }, [onDone]);

  return (
    <div className={`flex flex-col gap-5 text-center transition-all duration-500 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
      {/* Success card */}
      <div className={`card-phone p-8 flex flex-col items-center gap-4 border-2 ${
        isClockedIn ? 'border-primary border-opacity-40' : 'border-amber-300 border-opacity-60'
      }`}>
        {/* Success icon */}
        <div className={`w-24 h-24 rounded-full flex items-center justify-center ${
          isClockedIn ? 'bg-blue-50 border-2 border-blue-200' : 'bg-amber-50 border-2 border-amber-200'
        }`}>
          {isClockedIn ? (
            <svg width="44" height="44" viewBox="0 0 24 24" fill="none" stroke="#1a4fa0" strokeWidth="2">
              <circle cx="12" cy="12" r="10"/>
              <polyline points="12 6 12 12 16 14"/>
            </svg>
          ) : (
            <svg width="44" height="44" viewBox="0 0 24 24" fill="none" stroke="#c05a00" strokeWidth="2">
              <circle cx="12" cy="12" r="10"/>
              <rect x="9" y="9" width="6" height="6"/>
            </svg>
          )}
        </div>

        {/* Status headline */}
        <div>
          <div className={`text-3xl font-bold mb-1 ${isClockedIn ? 'text-primary' : 'text-amber-700'}`}>
            {isClockedIn ? 'CLOCKED IN ✓' : 'CLOCKED OUT ✓'}
          </div>
          <p className="text-muted-foreground text-sm">Hello, {employee.name}</p>
        </div>

        {/* Recorded time */}
        <div className={`w-full rounded-xl px-5 py-4 ${
          isClockedIn ? 'bg-blue-50 border border-blue-200' : 'bg-amber-50 border border-amber-200'
        }`}>
          <p className="text-xs font-semibold text-muted-foreground uppercase tracking-widest mb-1">
            Official time recorded
          </p>
          <p className={`text-4xl font-bold tabular-nums ${isClockedIn ? 'text-primary' : 'text-amber-700'}`}>
            {recordedTime}
          </p>
          <p className="text-xs text-muted-foreground mt-1">
            Server-authoritative · America/New_York
          </p>
        </div>

        <p className="text-xs text-muted-foreground">
          This screen closes automatically in a few seconds
        </p>
      </div>

      {/* Session summary */}
      {sessions.length > 0 && (
        <div className="card-phone p-4">
          <p className="text-xs font-semibold text-muted-foreground uppercase tracking-widest mb-3">
            Today&apos;s Record
          </p>
          <div className="flex flex-col gap-2">
            {sessions.map((session, idx) => (
              <div
                key={session.id}
                className="flex items-center gap-3 py-2 px-3 rounded-lg bg-secondary"
              >
                <div className="w-5 h-5 rounded-full bg-primary flex items-center justify-center flex-shrink-0">
                  <span className="text-white text-xs font-bold">{idx + 1}</span>
                </div>
                <div className="flex items-center gap-2 text-sm font-semibold text-foreground tabular-nums flex-1">
                  <span>{session.clockIn}</span>
                  <span className="text-muted-foreground">→</span>
                  <span>{session.clockOut ?? 'Active'}</span>
                </div>
                {session.duration && session.duration !== 'Calculating...' && (
                  <span className="text-xs text-muted-foreground">{session.duration}</span>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      <button
        onClick={onDone}
        className="text-sm text-muted-foreground hover:text-primary transition-colors py-2"
      >
        Done
      </button>
    </div>
  );
}